import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, SelectMenu, Table, TableColumn, Tooltip, Typography } from '@scale/scaleui';
import { PlatinumQualityStatus } from '@scale/shared-types';
import { QueueRunJSON } from '@scale/shared/types/queueV2/queueExplanation';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import QualityDashboard from 'react-scale/components/Expert/PlatinumTasker/components/QualityDashboard';
import { UsersChatBulkAuditModal } from 'react-scale/components/ExpertManagement/ExpertManagementPage/BulkAudit/UsersChatBulkAuditModal';
import { getActionStatus } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ExpertTableRequiredAction';
import { ToolsLink } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ToolsLink';
import { AUTO_HIDEABLE_COLUMNS } from 'react-scale/components/ExpertManagement/constants';
import { useFetchAuditCoverageForSquad } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchAuditCoverageForSquad';
import { useCanUserModifyCommunity } from 'react-scale/components/ExpertManagement/hooks/useCanUserModifyCommunity';
import { useTableFilteringContext } from 'react-scale/components/ExpertManagement/hooks/useTableFiltering';
import {
  useUserInfoStore,
  userInfoSelector,
} from 'react-scale/components/ExpertManagement/stores/userInfo.store';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import { makeDatetimeCellFunction } from 'react-scale/components/table/cellFunctions';
import { DownloadAsCsvButton } from 'react-scale/corp/components/UI/DownloadAsCsvButton';
import { TableColumnDisplayOrderConfig } from 'react-scale/corp/components/UI/TableColumnDisplayOrder/TableColumnDisplayOrderConfig';
import { useTableColumnDisplayOrder } from 'react-scale/corp/components/UI/TableColumnDisplayOrder/useTableColumnDisplayOrder';
import FilterComponent from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/SharedComponents/FilterComponent';
import { shallow } from 'zustand/shallow';
import { useExpertActionsState } from '../hooks/useExpertActionsState';
import { LoadingTable } from './LoadingTable';
import { MoveToNewManagerModal } from './MoveToNewManagerModal';
import { rerunQueueForWorker } from './api';

enum LastQueueRunResult {
  EmptyQueue = 'Empty Queue',
  AssignmentFound = 'Assignment Found',
}

const DatetimeCellFunction = makeDatetimeCellFunction({
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});

const COLUMN_PRESETS = [
  {
    id: 'activity',
    label: 'Show task activity only',
    columns: [
      'name',
      'workerTeam',
      'lastQueueRun.requestedAt',
      'countDays',
      'lastActive',
      'countAttempts',
      'lastAttempt.attemptedAt',
      'attemptHours',
      'countReviews',
      'reviewHours',
    ],
  },
  {
    id: 'quality',
    label: 'Show quality columns only',
    columns: ['name', 'workerTeam', 'gqaScore', 'speedAuditAverage', 'averageBmAccuracy'],
  },
];

function expertRowToCsvRow(row: any) {
  const newRow = {
    ...row,
    workerTeam: row.workerTeam?.name,
    workerManager: row.workerManager?.name,
    lastQueueRun: row.lastQueueRun
      ? new Date(row.lastQueueRun.requestedAt).toLocaleDateString()
      : '',
    lastQueueRunAssignments: row.lastQueueRun?.assignments?.length || 0,
  };
  delete newRow.workerSkillEntries;
  delete newRow.qualityScores;
  return newRow;
}

interface ExpertTableProps {
  userId: string;
  title?: string;
  data: object[] | null;
  isLoading: boolean;
  reloadData: () => void;
  showPlatinum: boolean;
}

export const ExpertTable: React.FC<ExpertTableProps> = ({
  userId,
  title = 'Experts Team',
  data,
  isLoading,
  reloadData,
  showPlatinum,
}: ExpertTableProps) => {
  const {
    rowSelectionState,
    setRowSelectionState,
    selectedRows,
    moveManagersModalOpen,
    setMoveManagersModalOpen,
    handleUnfollowUsers,
    handleAssignNewManagers,
    isModifying,
  } = useExpertActionsState(data, reloadData);

  const canModify = useCanUserModifyCommunity();

  const userInfo = useUserInfoStore(userInfoSelector, shallow);
  const { expertSlackChannels, remoSlackChannels } = userInfo!;

  const [newQueueRunLoading, setNewQueueRunLoading] = useState<{ [id: string]: boolean }>({});
  const [newQueueRuns, setNewQueueRuns] = useState<{ [id: string]: QueueRunJSON }>({});
  const [openBulkAuditModal, setOpenBulkAuditModal] = useState<boolean>(false);

  const csvData = useMemo(() => (data || []).map(expertRowToCsvRow), [data]);

  const handleRerunQueue = useCallback(
    async (workerId: string) => {
      setNewQueueRunLoading(state => ({ ...state, [workerId]: true }));
      const result = await rerunQueueForWorker(workerId);
      setNewQueueRuns(state => ({ ...state, [workerId]: result.result }));
      setNewQueueRunLoading(state => ({ ...state, [workerId]: false }));
    },
    [setNewQueueRunLoading, setNewQueueRuns],
  );

  const showDolphin = useMemo(
    () => (data || []).some((row: any) => row.dolphinQaTaskMetrics?.count),
    [data],
  );

  const ExpertDataColumns: TableColumn[] = [
    {
      id: 'name',
      header: 'Name',
      cell: function ExpertName(props: any) {
        const { name, id } = props.row.original;
        if (!name) {
          return null;
        }
        return (
          <Link to={buildRoute(Routes.ExpertManagement, { user: id })} className="flex flex-col">
            <div>{name}</div>
            <div className="text-xs">{id}</div>
          </Link>
        );
      },
    },
    {
      id: 'workerTeam.name',
      header: 'Tasker Team',
      cell: function WorkerTeam(props: any) {
        const { workerTeam } = props.row.original;
        if (!workerTeam?.name) {
          return null;
        }
        const parts = workerTeam.name.split('/');
        const line1 = parts.slice(0, 1).join('/');
        const line2 = parts.slice(1).join('/');
        return (
          <ToolsLink to={`/team/${workerTeam.id}/overview`} className="flex flex-col text-xs">
            <div>{line1}</div>
            <div>{line2}</div>
          </ToolsLink>
        );
      },
    },
    {
      id: 'lastQueueRunSort',
      header: 'Last Queue Run',
      cell: function LastQueueRun(props: any) {
        const { lastQueueRun } = props.row.original;
        if (!lastQueueRun) {
          return null;
        }
        const queueRun = newQueueRuns[lastQueueRun.user] ?? lastQueueRun;
        if (!queueRun) {
          return null;
        }
        const lookupId = queueRun.assignments?.length
          ? queueRun.assignments[0].projectId || queueRun.assignments[0]._id
          : null;
        const displayName = queueRun.assignments?.length
          ? queueRun.assignments[0].name ?? 'Assignment Found'
          : 'No Assignment';
        return (
          <div className="flex flex-col text-xs">
            {lookupId ? (
              <ToolsLink to={`lookup/${lookupId}`} className="flex items-center">
                <Tooltip
                  text={displayName.length < 20 ? '' : displayName}
                  target={
                    <div>
                      <Typography.Subtitle4>
                        {displayName.length < 20 ? displayName : displayName.slice(0, 17) + '...'}
                      </Typography.Subtitle4>
                      <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
                    </div>
                  }
                />
              </ToolsLink>
            ) : (
              <Typography.Subtitle4>{displayName}</Typography.Subtitle4>
            )}

            <div>
              {new Date(queueRun.requestedAt).toLocaleString()}{' '}
              <Tooltip
                targetClassName="inline"
                text="Rerun queue for this user"
                target={
                  <div>
                    <Button
                      size="sm"
                      variant="transparent"
                      onClick={() => handleRerunQueue(queueRun.user)}
                      disabled={newQueueRunLoading[queueRun.user]}
                      className="p-0"
                    >
                      <FontAwesomeIcon icon="redo" style={{ marginLeft: 2, fontSize: 9 }} />
                    </Button>
                  </div>
                }
              />
            </div>
          </div>
        );
      },
    },
    {
      id: 'countAttempts',
      header: 'Tasks',
      cell: function Tasks(props: any) {
        const { countAttempts, countReviews } = props.row.original;
        return (
          <div className="flex flex-col">
            <div className="text-sm">
              <span className="font-medium">{countAttempts}</span>
            </div>
            <div className="text-xs">
              {countAttempts - countReviews} attempts / {countReviews} reviews
            </div>
          </div>
        );
      },
    },
    {
      id: 'attemptHours',
      header: 'Hours',
      cell: function Hours(props: any) {
        const { attemptHours, countAttempts, reviewHours } = props.row.original;
        return (
          <div className="flex flex-col">
            <div className="text-sm">
              <span className="font-medium">{Math.round(attemptHours * 100) / 100} hours</span>
            </div>
            <div className="text-xs">
              attempts: {Math.round((attemptHours - reviewHours) * 100) / 100} hrs / reviews:{' '}
              {Math.round(reviewHours * 100) / 100} hrs
            </div>
            <div className="text-xs">
              {Math.round(countAttempts ? (attemptHours / countAttempts) * 60 : 0)} mins / task
            </div>
          </div>
        );
      },
    },
    {
      id: 'lastAttempt.attemptedAt',
      header: 'Last Attempt',
      cell: function LastAttempt(props: any) {
        const { lastAttempt } = props.row.original;
        if (!lastAttempt) {
          return null;
        }
        return (
          <div className="flex flex-col">
            <ToolsLink to={`/lookup/${lastAttempt._id}`} className="flex items-center">
              <Typography.Subtitle4>Lookup</Typography.Subtitle4>
              <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
            </ToolsLink>
            <div className="text-xs">{new Date(lastAttempt.attemptedAt).toLocaleString()}</div>
          </div>
        );
      },
    },
    {
      id: 'averageBmAccuracy',
      header: 'Avg BM Accuracy',
      cell: function AverageBmAccuracy(props: any) {
        const { averageBmAccuracy } = props.row.original;
        if (!averageBmAccuracy) {
          return null;
        }
        return `${Math.round(averageBmAccuracy)}%`;
      },
    },
    {
      id: 'qmsScore',
      header: 'QMS Score',
      headerTooltip: 'Score ranges from 1-5',
      cell: function QmsScore(props: any) {
        const { qmsScore } = props.row.original;
        if (_.isNil(qmsScore) || qmsScore === 0) {
          return null;
        }
        return `${_.round(qmsScore, 1)}/5`;
      },
    },
    {
      id: 'sbqPercentage',
      header: 'SBQ%',
      cell: function SbqPercentage(props: any) {
        const { sbqPercentage } = props.row.original;
        if (sbqPercentage === null) {
          return null;
        }
        return `${sbqPercentage}%`;
      },
    },
    {
      id: 'gqaScore',
      header: 'QC Score',
      headerTooltip: 'Score ranges from 1-5',
      cell: function GqaScore(props: any) {
        const { gqaScore } = props.row.original;
        if (!gqaScore?.average) {
          return null;
        }
        return `${Math.round(gqaScore.average * 100) / 100}`;
      },
    },
    {
      id: 'gqaPdr',
      header: 'QC PDR',
      headerTooltip: 'The percentage of QC audits rated as 1/5 or 2/5',
      cell: function GqaPdr(props: any) {
        const { gqaPdr } = props.row.original;
        if (_.isNil(gqaPdr)) {
          return null;
        }
        return `${_.round(gqaPdr * 100)}%`;
      },
    },
    {
      id: 'gqaCount',
      header: 'QC Count',
      cell: function GqaCount(props: any) {
        const { gqaScore } = props.row.original;
        return gqaScore.count;
      },
    },
    {
      id: 'speedAuditAverage',
      header: 'Internal Audit Score',
      headerTooltip: 'Score ranges from 1-5',
      cell: function SpeedAuditScore(props: any) {
        const { speedAuditAverage } = props.row.original;
        if (_.isNil(speedAuditAverage?.average)) {
          return null;
        }
        return `${Math.round(speedAuditAverage?.average * 100) / 100}`;
      },
    },
    {
      id: 'speedAuditPdr',
      header: 'Internal Audit PDR',
      headerTooltip: 'The percentage of audits resulting in a 2 or 1',
      cell: function SpeedAuditPdr(props: any) {
        const { speedAuditPdr } = props.row.original;
        if (_.isNil(speedAuditPdr)) {
          return null;
        }
        return `${_.round(speedAuditPdr * 100)}%`;
      },
    },
    {
      id: 'speedAuditCount',
      header: 'Internal Audit Count',
      cell: function SpeedAuditCount(props: any) {
        const { speedAuditAverage } = props.row.original;
        return speedAuditAverage.count;
      },
    },
    {
      id: 'gqaAndSpeedAuditScore',
      header: 'QC + InternalAudit Score',
      headerTooltip: 'Average of all QC and Speed Audit Scores',
      cell: function GqaAndSpeedAuditScore(props: any) {
        const { gqaAndSpeedAuditScore } = props.row.original;
        if (_.isNil(gqaAndSpeedAuditScore?.average)) {
          return null;
        }
        return `${_.round(gqaAndSpeedAuditScore.average, 2)}`;
      },
    },
    {
      id: 'gqaAndSpeedAuditCount',
      header: 'QC + InternalAudit Count',
      headerTooltip: 'Count of all QC and Speed Audit Scores',
      cell: function GqaAndSpeedAuditCount(props: any) {
        const { gqaAndSpeedAuditScore } = props.row.original;
        if (_.isNil(gqaAndSpeedAuditScore?.count)) {
          return null;
        }
        return `${gqaAndSpeedAuditScore.count}`;
      },
    },
    {
      id: 'averageSpeedZScore',
      header: 'Speed Z-score',
    },
    {
      id: 'fractalGuardianScore',
      header: 'Fractal Guardian',
      headerTooltip: 'Score ranges from 1-5',
      cell: function FractalGuardianScore(props: any) {
        const { fractalGuardianScore } = props.row.original;
        if (!fractalGuardianScore) {
          return null;
        }
        return `${Math.round(fractalGuardianScore * 10) / 10}`;
      },
    },
    {
      id: 'countDays',
      header: 'Active Days',
    },
    {
      id: 'lastOnline',
      header: 'Last Online',
      cell: DatetimeCellFunction,
    },
    {
      id: 'lastActive',
      header: 'Last Active',
      cell: DatetimeCellFunction,
    },
  ];

  const DolphinDataColumns: TableColumn[] = [
    {
      id: 'dolphinQaTaskMetrics.count',
      header: 'QA Tasks',
    },
    {
      id: 'dolphinQaTaskMetrics.averagePromptQualityScore',
      header: 'Avg Prompt Quality',
      headerTooltip: 'Score ranges from 0-3',
    },
    {
      id: 'dolphinQaTaskMetrics.averageResponseAgreement',
      header: 'Avg Response Agreement',
      cell: function AvgResponseAgreement(props: any) {
        const { averageResponseAgreement } = props.row.original.dolphinQaTaskMetrics;
        if (_.isNil(averageResponseAgreement)) {
          return null;
        }
        return `${Math.round(averageResponseAgreement)}%`;
      },
    },
    {
      id: 'dolphinQaTaskMetrics.averageSystemInstructionsAgreement',
      header: 'Avg SI Agreement',
      cell: function AvgSystemInstructionsAgreement(props: any) {
        const { averageSystemInstructionsAgreement } = props.row.original.dolphinQaTaskMetrics;
        if (_.isNil(averageSystemInstructionsAgreement)) {
          return null;
        }
        return `${Math.round(averageSystemInstructionsAgreement)}%`;
      },
    },
  ];

  const PlatinumDataColumns: TableColumn[] = [
    {
      id: 'platinumQualityStatus',
      header: 'Platinum Status',
      cell: function PlatinumQualityStatus(props: any) {
        const { platinumQualityStatus } = props.row.original;
        return <QualityDashboard qualityStatus={platinumQualityStatus} compact />;
      },
    },
  ];

  const columnsToUse = [
    ...ExpertDataColumns,
    ...(showDolphin ? DolphinDataColumns : []),
    ...(showPlatinum ? PlatinumDataColumns : []),
  ];

  const { data: auditStatus } = useFetchAuditCoverageForSquad(userId);

  const displayState = useTableColumnDisplayOrder('expert-table', columnsToUse);
  const { isColumnVisible, toggleVisibility } = displayState;
  // hack due to scaleui not supporting sortFn for columns
  const enrichedData = useMemo(
    () =>
      (data ?? [])
        .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        .map((row: any) => ({
          ...row,
          lastQueueRunSort: `${
            row.lastQueueRun?.assignments?.length > 0 ? 'a' : 'b'
          }-${row.lastQueueRun?.requestedAt.valueOf()}`,
          lastQueueRunResult: row.lastQueueRun?.assignments?.length
            ? LastQueueRunResult.AssignmentFound
            : LastQueueRunResult.EmptyQueue,
          workerTeamName: row.workerTeam?.name,
          dolphinCount: row.dolphinQaTaskMetrics?.count,
          dolphinAveragePromptQualityScore: row.dolphinQaTaskMetrics?.averagePromptQualityScore,
          dolphinAverageResponseAgreement: row.dolphinQaTaskMetrics?.averageResponseAgreement,
          dolphinAverageSystemInstructionsAgreement:
            row.dolphinQaTaskMetrics?.averageSystemInstructionsAgreement,
          platinumQualityStatus: row.platinumInfo?.qualityStatus ?? PlatinumQualityStatus.Pending,
          actionStatus: getActionStatus(
            row as any,
            expertSlackChannels,
            remoSlackChannels,
            auditStatus?.[row.id] ?? null,
          ),
        })),
    [auditStatus, data, expertSlackChannels, remoSlackChannels],
  );

  let additionalFilters: { [key: string]: { name: string; type: 'number' | 'fuzzy' | 'exact' } } =
    {};

  if (showDolphin) {
    additionalFilters = {
      ...additionalFilters,
      dolphinCount: { name: 'Dolphin QA Tasks', type: 'number' },
      dolphinAveragePromptQualityScore: { name: 'Dolphin Avg Prompt Quality', type: 'number' },
      dolphinAverageResponseAgreement: {
        name: 'Dolphin Avg Response Agreement',
        type: 'number',
      },
      dolphinAverageSystemInstructionsAgreement: {
        name: 'Dolphin Avg SI Agreement',
        type: 'number',
      },
    };
  }

  if (showPlatinum) {
    additionalFilters = {
      ...additionalFilters,
      platinumQualityStatus: {
        name: 'Platinum Quality Status',
        type: 'exact',
      },
    };
  }

  useEffect(() => {
    if (data) {
      for (const column of AUTO_HIDEABLE_COLUMNS) {
        if (
          data.every((row: any) => row[column] === null || row[column] === undefined) &&
          isColumnVisible(column)
        ) {
          toggleVisibility(column);
        } else if (
          !data.every((row: any) => row[column] === null || row[column] === undefined) &&
          !isColumnVisible(column)
        ) {
          toggleVisibility(column);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, toggleVisibility]);

  const handleManageUsersMenu = useCallback(
    (option: any) => {
      switch (option) {
        case 'unfollow':
          handleUnfollowUsers();
          break;
        case 'moveusers':
          handleAssignNewManagers();
          break;
      }
    },
    [handleUnfollowUsers, handleAssignNewManagers],
  );

  const { filters, filteredData, fieldMap, setFilters, exactFieldOptionsMap, fieldOptions } =
    useTableFilteringContext({
      key: 'expert-management.tableFilters-' + userId,
      data: enrichedData,
      fieldFilters: {
        id: { name: 'User ID', type: 'fuzzy' },
        name: { name: 'Name', type: 'fuzzy' },
        workerTeamName: { name: 'Tasker Team', type: 'fuzzy' },
        attemptHours: { name: 'Hours', type: 'number' },
        lastQueueRunResult: { name: 'Last Queue Run', type: 'exact' },
        countAttempts: { name: 'Tasks', type: 'number' },
        gqaScore: { name: 'Quality Control Score', type: 'number' },
        speedAuditAverage: { name: 'Speed Audit Avg', type: 'number' },
        averageBmAccuracy: { name: 'Avg BM Accuracy', type: 'number' },
        averageSpeedZScore: { name: 'Speed Z-score', type: 'number' },
        sbqPercentage: { name: 'SBQ%', type: 'number' },
        countDays: { name: 'Active Days', type: 'number' },
        ...additionalFilters,
      },
      exactFieldOptionsMap: {
        lastQueueRunResult: [LastQueueRunResult.EmptyQueue, LastQueueRunResult.AssignmentFound],
        platinumQualityStatus: [
          PlatinumQualityStatus.OnTrack,
          PlatinumQualityStatus.Pending,
          PlatinumQualityStatus.FirstNotice,
          PlatinumQualityStatus.SecondNotice,
          PlatinumQualityStatus.ThirdNotice,
        ],
      },
    });

  return (
    <>
      <MoveToNewManagerModal
        open={moveManagersModalOpen}
        onClose={() => setMoveManagersModalOpen(false)}
        expertIds={selectedRows.map((row: any) => row.id)}
      />
      <UsersChatBulkAuditModal
        isOpen={openBulkAuditModal}
        onClose={() => setOpenBulkAuditModal(false)}
        experts={selectedRows.map((row: any) => ({ id: row.id, name: row.name }))}
      />
      <div className="flex flex-col gap-4 scaleui scale-tailwind">
        <div className="flex items-center gap-4 w-full">
          <Typography.Subtitle2 className="whitespace-nowrap">{title}</Typography.Subtitle2>
          <div className="flex items-center gap-2 w-full">
            {canModify && (
              <SelectMenu
                disabled={selectedRows.length === 0}
                variant="minimal"
                options={[
                  {
                    id: 'none',
                    name: 'Actions for Selected Users',
                  },
                  {
                    id: 'unfollow',
                    name: 'Remove Users',
                  },
                  {
                    id: 'moveusers',
                    name: 'Assign New Leader',
                  },
                ]}
                selectedOptionId="none"
                setSelected={handleManageUsersMenu}
              />
            )}
            <TableColumnDisplayOrderConfig context={displayState} presets={COLUMN_PRESETS} />
            <DownloadAsCsvButton data={csvData} filename="expert-table-data.csv" />
            <div className="flex ml-auto">
              <Button disabled={!selectedRows.length} onClick={() => setOpenBulkAuditModal(true)}>
                Create Audit
              </Button>
            </div>
          </div>
        </div>
        <div>
          <FilterComponent
            filters={filters}
            setFilters={setFilters}
            fieldMap={fieldMap}
            exactFieldOptionsMap={exactFieldOptionsMap}
            fieldOptions={fieldOptions}
          />
        </div>
        {isLoading || isModifying ? (
          <LoadingTable columns={displayState.displayColumns} numRows={3} rowHeight={2} />
        ) : (
          <Table
            columns={displayState.displayColumns}
            data={filteredData}
            rowSelectionState={rowSelectionState}
            onRowSelectionStateChange={s => setRowSelectionState(s)}
            enableRowSelection
            defaultPageSize={50}
            freezeFirstColumn
          />
        )}
      </div>
    </>
  );
};
